import axios from "axios";

export const LIVE_TIMEFRAMES = ["15m", "1h", "4h", "1d", "1w"];

export const LIVE_AI_ITEMS = LIVE_TIMEFRAMES.map((key) => ({ label: key, key }));

const trainAsked = new Set();
const PIVOT_CACHE_KEY = "cc_live_pivot_cache_v1";
const DAY_MS = 24 * 60 * 60 * 1000;

const initialState = {
  suggestions: [],
  status: "",
  errorMessage: "",
  coinStatus: {},
  liveTimeframe: "15m",
  macroRegime: null,
  usPreset: "all",
  refreshTick: 0,
};

function browserStorage() {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch (error) {
    return null;
  }
}

function pivotTtlMs(timeframe) {
  const frame = normalizeLiveTimeframe(timeframe);
  if (frame === "1d" || frame === "1w") return DAY_MS;
  if (frame === "4h") return 4 * 60 * 60 * 1000;
  if (frame === "1h") return 60 * 60 * 1000;
  return 15 * 60 * 1000;
}

function cacheSymbolKey(item) {
  const symbol = String(item?.symbol || "")
    .split("/")[0]
    .toUpperCase();
  return `${symbol}|${normalizeLiveTimeframe(item?.timeframe)}`;
}

function freshCached(savedAt, timeframe) {
  const saved = Number(savedAt);
  return saved > 0 && Date.now() - saved < pivotTtlMs(timeframe);
}

function publicCached(item) {
  if (!item || typeof item !== "object") return item;
  const copy = { ...item };
  delete copy.savedAt;
  return copy;
}

function readRawPivotCache(storage) {
  try {
    const parsed = JSON.parse(storage.getItem(PIVOT_CACHE_KEY) || "");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
}

export function readPivotCache() {
  const storage = browserStorage();
  if (!storage) return { suggestions: [], macroRegime: null };
  const raw = readRawPivotCache(storage);
  const suggestions = (raw.suggestions || []).filter(
    (item) => freshCached(item?.savedAt, item?.timeframe) && item?.prediction && !item?.miss && !item?.error,
  );
  const macro = raw.macroRegime;
  const macroRegime = macro && freshCached(macro.savedAt, "1d") && macro.leader ? macro : null;
  return { suggestions, macroRegime };
}

function writePivotCache(suggestions, macroRegime) {
  const storage = browserStorage();
  if (!storage) return;
  try {
    const prev = readRawPivotCache(storage);
    const prevByKey = {};
    (prev.suggestions || []).forEach((item) => {
      prevByKey[cacheSymbolKey(item)] = item;
    });
    const now = Date.now();
    const byKey = {};
    (suggestions || []).forEach((item) => {
      if (!item?.prediction || item.miss || item.error) return;
      byKey[cacheSymbolKey(item)] = item;
    });
    const nextSuggestions = Object.keys(byKey).map((key) => {
      const item = byKey[key];
      const old = prevByKey[key];
      const same = old && old.prediction === item.prediction && old.symbol === item.symbol;
      const savedAt = same && freshCached(old.savedAt, item.timeframe) ? old.savedAt : now;
      return { ...publicCached(item), savedAt };
    });
    let nextMacro = prev.macroRegime && freshCached(prev.macroRegime.savedAt, "1d") ? prev.macroRegime : null;
    if (macroRegime?.leader) {
      const old = prev.macroRegime;
      const same = old && old.leader === macroRegime.leader && old.summary === macroRegime.summary;
      const savedAt = same && freshCached(old.savedAt, "1d") ? old.savedAt : now;
      nextMacro = { ...publicCached(macroRegime), savedAt };
    }
    storage.setItem(
      PIVOT_CACHE_KEY,
      JSON.stringify({ suggestions: nextSuggestions, macroRegime: nextMacro }),
    );
  } catch (error) {
    return;
  }
}

function loadInitialLiveState() {
  const stored = readPivotCache();
  return {
    ...initialState,
    suggestions: stored.suggestions.map(publicCached),
    macroRegime: stored.macroRegime ? publicCached(stored.macroRegime) : null,
  };
}

export function cachedLiveSuggestion(symbol, timeframe) {
  const saved = liveSuggestionFor(readPivotCache().suggestions, symbol, timeframe);
  if (!saved?.prediction || saved.miss || saved.error) return null;
  return publicCached(saved);
}

export function rememberLiveSuggestion(suggestion) {
  if (!suggestion?.prediction || suggestion.miss || suggestion.error) return;
  const current = readPivotCache();
  writePivotCache([...(current.suggestions || []), suggestion], current.macroRegime);
}

function withPivotCache(state) {
  writePivotCache(state.suggestions, state.macroRegime);
  return state;
}

export function normalizeLiveTimeframe(timeframe) {
  const value = String(timeframe || "");
  return LIVE_TIMEFRAMES.includes(value) ? value : "15m";
}

export function liveSuggestionFor(suggestions, coin, timeframe) {
  const name = String(coin || "")
    .split("/")[0]
    .toUpperCase();
  const wanted = normalizeLiveTimeframe(timeframe);
  return (suggestions || []).find((item) => {
    const symbol = String(item?.symbol || "")
      .split("/")[0]
      .toUpperCase();
    if (symbol !== name) return false;
    return normalizeLiveTimeframe(item?.timeframe) === wanted;
  });
}

export function pivotsSettled(targets, suggestions, coinStatus, timeframe) {
  const list = targets || [];
  if (!list.length) return true;
  const frame = normalizeLiveTimeframe(timeframe);
  const status = coinStatus || {};
  return list.every((target) => {
    const symbol = target?.symbol;
    if (!symbol) return true;
    const wanted = target.stock ? "1d" : frame;
    if (liveSuggestionFor(suggestions, symbol, wanted)) return true;
    const stem = String(symbol).split("/")[0];
    const state = status[symbol] || status[stem] || status[stem.toUpperCase()];
    return state === "error";
  });
}

export const suggestionReducer = (state = loadInitialLiveState(), action) => {
  const { type, payload } = action;

  switch (type) {
    case "get_suggestions_request":
      return {
        ...state,
        status: "loading",
        errorMessage: "",
        coinStatus: {
          ...state.coinStatus,
          [`${payload.symbol}`]: "loading",
          // [`${payload.symbol}-${payload.exchange_id}`]: "loading",
        },
      };

    case "set_live_ai_timeframe":
      return {
        ...state,
        liveTimeframe: normalizeLiveTimeframe(payload),
      };

    case "set_us_preset":
      return {
        ...state,
        usPreset: payload || "all",
      };

    case "set_macro_regime":
      return withPivotCache({
        ...state,
        macroRegime: payload || null,
      });

    case "clear_live_symbols": {
      const names = new Set(
        (payload || []).map((symbol) => String(symbol || "").split("/")[0].toUpperCase()),
      );
      const coinStatus = { ...state.coinStatus };
      names.forEach((name) => {
        delete coinStatus[name];
      });
      (payload || []).forEach((symbol) => {
        delete coinStatus[symbol];
      });
      return withPivotCache({
        ...state,
        suggestions: (state.suggestions || []).filter(
          (item) => !names.has(String(item?.symbol || "").split("/")[0].toUpperCase()),
        ),
        coinStatus,
        refreshTick: (state.refreshTick || 0) + 1,
      });
    }

    case "get_suggestions_success": {
      const savedTimeframe = normalizeLiveTimeframe(payload.timeframe);
      const updatedSuggestion = (state.suggestions || []).filter((item) => {
        if (item.symbol !== payload.symbol) return true;
        return normalizeLiveTimeframe(item.timeframe) !== savedTimeframe;
      });

      return withPivotCache({
        ...state,
        status: "success",
        suggestions: [...updatedSuggestion, { ...payload, timeframe: savedTimeframe }],
        coinStatus: {
          ...state.coinStatus,
          [`${payload.symbol}`]: "success",
          // [`${payload.symbol}-${payload.exchange_id}`]: "success",
        },
      });
    }

    case "get_suggestions_error":
      return {
        ...state,
        status: "error",
        coinStatus: {
          ...state.coinStatus,
          [`${payload?.symbol}`]: "error",
          // [`${payload.symbol}-${payload.exchange_id}`]: "error"
        },
      };

    case "live-suggestion-error":
      return {
        ...state,
        coinStatus: {
          ...state.coinStatus,
          [`${payload.symbol}`]: "error",
        },
      };

    case "get_suggestion_canceled":
      return {
        ...state,
        coinStatus: {
          ...state.coinStatus,
          [`${payload.symbol}`]: "",
        },
      };

    case "update_suggestion": {
      const coin = payload.symbol?.split("/")?.[0];

      const savedTimeframe = normalizeLiveTimeframe(payload.timeframe);
      const suggestions = (state.suggestions || []).filter((item) => {
        if (item.symbol?.split("/")?.[0] !== coin) return true;
        return normalizeLiveTimeframe(item.timeframe) !== savedTimeframe;
      });

      const newSuggestion = { ...payload, symbol: coin, timeframe: savedTimeframe };

      return withPivotCache({
        ...state,
        suggestions: [...suggestions, newSuggestion],
        coinStatus: {
          ...state.coinStatus,
          [`${coin}`]: "success",
        },
      });
    }

    default:
      return state;
  }
};

export const updateSuggestions = (suggestion) => (dispatch) => {
  dispatch({ type: "update_suggestion", payload: suggestion });
};

export const setLiveAiTimeframe = (timeframe) => ({
  type: "set_live_ai_timeframe",
  payload: timeframe,
});

export const setUsPreset = (preset) => ({
  type: "set_us_preset",
  payload: preset,
});

export const clearLiveSymbols = (symbols) => ({
  type: "clear_live_symbols",
  payload: symbols,
});

const PIVOT_URL = "https://akqyv95sd7.execute-api.ap-southeast-2.amazonaws.com/dev/pivot";
const PIVOT_CACHE_FIELDS = [
  "action",
  "symbol",
  "timeframe",
  "exchange_id",
  "market",
  "q",
  "do_backtest",
  "shift_minutes",
];

export function pivotApiUrl(body) {
  const params = new URLSearchParams();
  PIVOT_CACHE_FIELDS.forEach((key) => {
    const value = body?.[key];
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });
  if (Array.isArray(body?.symbols) && body.symbols.length) {
    params.set("symbols", body.symbols.join(","));
  }
  if (body?.ai_trade_suggestion) {
    params.set("ai_trade_suggestion", "true");
    if (body.user) params.set("user", String(body.user));
  }
  const frame = String(body?.timeframe || "");
  const daily = frame === "1d" || frame === "1w" || body?.action === "analyze_market_fast";
  const path = daily ? `${PIVOT_URL}/day` : PIVOT_URL;
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

function pivotBody(data) {
  if (data && typeof data.body === "string") {
    try {
      return JSON.parse(data.body);
    } catch (error) {
      return {};
    }
  }
  return data || {};
}

function stockRowMatches(item, symbol) {
  const label = String(item?.symbol || "");
  return label === symbol || label.toUpperCase() === symbol.toUpperCase();
}

function askDailyTrain(symbol, rowSymbol) {
  const pair = String(rowSymbol || symbol).toUpperCase();
  if (trainAsked.has(pair)) return;
  trainAsked.add(pair);
  axios
    .post(pivotApiUrl({ action: "request_train", symbol: rowSymbol || symbol, timeframe: "1d" }), {
      action: "request_train",
      symbol: rowSymbol || symbol,
    })
    .catch(() => {
      trainAsked.delete(pair);
    });
}

function liveSignalsOpen(getState) {
  const profile = getState().profile?.profileData;
  return profile?.insai_active === "True" || profile?.myais_active === "True";
}

export const getMarketAnalysis = (user, email, symbols) => async (dispatch, getState) => {
  if (!liveSignalsOpen(getState)) return;
  const names = (symbols || []).map((symbol) => String(symbol || "").trim()).filter(Boolean);
  const missing = [];
  names.forEach((symbol) => {
    const saved = cachedLiveSuggestion(symbol, "1d");
    if (saved) {
      dispatch({
        type: "get_suggestions_success",
        payload: {
          ...saved,
          symbol,
          timeframe: "1d",
          exchange_id: "yahoo",
        },
      });
      return;
    }
    missing.push(symbol);
    dispatch({ type: "get_suggestions_request", payload: { symbol } });
  });
  const savedMacro = readPivotCache().macroRegime;
  if (savedMacro) {
    dispatch({ type: "set_macro_regime", payload: publicCached(savedMacro) });
  }
  if (!missing.length && savedMacro) return;
  const fallback = (symbol) => {
    dispatch(
      getLiveSuggestion(
        user,
        email,
        "yahoo",
        symbol,
        false,
        null,
        false,
        false,
        0,
        [],
        "1d",
        true,
      ),
    );
  };
  try {
    const marketBody = { action: "analyze_market_fast", symbols: missing, user, email };
    const res = await axios.post(pivotApiUrl(marketBody), marketBody, { timeout: 28000 });
    const body = pivotBody(res?.data);
    if (body?.status === "locked") {
      missing.forEach((symbol) => {
        dispatch({
          type: "get_suggestions_success",
          payload: {
            status: "locked",
            symbol,
            timeframe: "1d",
            exchange_id: "yahoo",
          },
        });
      });
      return;
    }
    if (body?.error || !Array.isArray(body?.symbols)) {
      missing.forEach(fallback);
      return;
    }
    if (body?.macro_regime) {
      dispatch({ type: "set_macro_regime", payload: body.macro_regime });
    }
    missing.forEach((symbol) => {
      const row = body.symbols.find((item) => stockRowMatches(item, symbol));
      if (!row || row.miss || row.price == null) {
        if (row?.miss) {
          dispatch({
            type: "get_suggestions_success",
            payload: {
              ...row,
              symbol,
              timeframe: "1d",
              exchange_id: "yahoo",
            },
          });
          if (row.miss === "no_model") {
            askDailyTrain(symbol, row.symbol);
          }
          return;
        }
        fallback(symbol);
        return;
      }
      dispatch({
        type: "get_suggestions_success",
        payload: {
          ...row,
          prediction: row.prediction || "Hold",
          symbol,
          timeframe: "1d",
          exchange_id: "yahoo",
        },
      });
      if (row.model === false) {
        askDailyTrain(symbol, row.symbol);
      }
    });
  } catch (error) {
    missing.forEach(fallback);
  }
};

const exchangePriorityList = ["kucoin", "binance", "kraken", "bybit", "gate"];

export const getLiveSuggestion =
  (
    user,
    email,
    exchange_id,
    symbolName,
    do_backtest = false,
    signal = null,
    ai_trade_suggestion = false,
    shift_minutes = false,
    retryCount = 0,
    triedExchanges = [],
    timeframe = null,
    stock = false,
  ) =>
  async (dispatch, getState) => {
    if (!liveSignalsOpen(getState)) return;
    const stockName = stock ? String(symbolName || "").trim() : "";
    const coin = stockName || symbolName?.split("/USDT")?.[0];
    const liveTimeframe = stock
      ? "1d"
      : LIVE_TIMEFRAMES.includes(timeframe)
        ? timeframe
        : null;

    if (symbolName?.includes("STETH")) {
      exchange_id = "bybit";
    }

    if (!do_backtest) {
      const cached = cachedLiveSuggestion(coin, liveTimeframe || "15m");
      if (cached) {
        dispatch({
          type: "get_suggestions_success",
          payload: {
            ...cached,
            exchange_id: stock ? "yahoo" : exchange_id,
            symbol: coin,
            timeframe: liveTimeframe || "15m",
          },
        });
        return;
      }
    }

    dispatch({
      type: "get_suggestions_request",
      payload: { symbol: coin, exchange_id },
    });

    let data = {
      action: "inference",
      exchange_id: stock ? "yahoo" : exchange_id,
      symbol: stockName || (symbolName?.includes("USDT") ? symbolName : `${symbolName}/USDT`),
      user,
      email,
      shift_minutes,
      ai_trade_suggestion,
    };

    if (liveTimeframe) {
      data.timeframe = liveTimeframe;
    }

    if (do_backtest) {
      data.do_backtest = "True";
    }

    const url = pivotApiUrl(data);

    await axios
      .post(url, data, { signal: signal })
      .then((res) => {
        if (res?.data?.status === "locked") {
          dispatch({
            type: "get_suggestions_success",
            payload: {
              status: "locked",
              exchange_id,
              symbol: coin,
              timeframe: liveTimeframe || "15m",
            },
          });
          return;
        }
        if (res?.data?.miss === "no_model") {
          const pair = String(data.symbol || "").toUpperCase();
          const saved = {
            ...res.data,
            exchange_id,
            symbol: coin,
            timeframe: liveTimeframe || "15m",
          };
          dispatch({ type: "get_suggestions_success", payload: saved });
          if (!trainAsked.has(pair)) {
            trainAsked.add(pair);
            axios
              .post(
                pivotApiUrl({
                  action: "request_train",
                  symbol: res?.data?.symbol || data.symbol,
                  timeframe: data.timeframe || "1d",
                }),
                { action: "request_train", symbol: res?.data?.symbol || data.symbol },
              )
              .then((trainRes) => {
                if (!trainRes?.data?.queued) {
                  trainAsked.delete(pair);
                  return;
                }
                dispatch({
                  type: "get_suggestions_success",
                  payload: {
                    ...saved,
                    message: "Training was requested.",
                    trainRequested: true,
                  },
                });
              })
              .catch(() => {
                trainAsked.delete(pair);
              });
          }
          return;
        }
        if (res?.data?.error) {
          if (stock) {
            dispatch({
              type: "live-suggestion-error",
              payload: { symbol: coin, exchange_id },
            });
            return;
          }
          const updatedTried = [...triedExchanges, exchange_id];
          const nextExchange = exchangePriorityList.find(
            (ex) => !updatedTried.includes(ex),
          );
          if (nextExchange && retryCount < 5) {
            dispatch(
              getLiveSuggestion(
                user,
                email,
                nextExchange,
                symbolName,
                do_backtest,
                signal,
                ai_trade_suggestion,
                shift_minutes,
                retryCount + 1,
                updatedTried,
                liveTimeframe,
              ),
            );
            return;
          }
          dispatch({
            type: "live-suggestion-error",
            payload: {
              symbol: coin,
              exchange_id,
            },
          });
          return;
        }
        dispatch({
          type: "get_suggestions_success",
          payload: {
            ...res?.data,
            exchange_id,
            symbol: coin,
            timeframe: liveTimeframe || "15m",
          },
        });
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          dispatch({
            type: "get_suggestion_canceled",
            payload: {
              symbol: coin,
              exchange_id,
            },
          });
          return;
        }
        dispatch({
          type: "live-suggestion-error",
          payload: {
            symbol: coin,
            exchange_id,
          },
        });
      });
  };
